/**
 * The simulated peer (`ProductOverview.md` §4.9).
 *
 * The product cannot be developed with two phones in hand at all times, so `?sim=1` spawns a
 * fake peer. The important part: it claims a real slot over the real REST endpoint and sends
 * its positions down a real WebSocket, so it exercises the entire pipeline rather than
 * short-circuiting it. From the app's point of view there is simply somebody there.
 *
 *   ?sim=1                        400 m to the north-east, walking towards you
 *   &simOffset=250                start this far away, in metres
 *   &simBearing=180               start in this direction from you
 *   &simSpeed=1.4                 metres per second
 *   &simBehaviour=approach        approach | wander | hold
 *   &simAccuracy=8                the accuracy it claims
 */

import {
  destinationPoint,
  haversineDistance,
  initialBearing,
  normalizeDegrees,
  POSITION,
  type LatLng,
} from '@beeline/shared';

import { api } from '../lib/api';

export type SimBehaviour = 'approach' | 'wander' | 'hold';

export interface SimOptions {
  offsetMetres: number;
  bearingDegrees: number;
  speedMetresPerSecond: number;
  behaviour: SimBehaviour;
  accuracyMetres: number;
}

export const DEFAULT_SIM: SimOptions = {
  offsetMetres: 400,
  bearingDegrees: 45,
  speedMetresPerSecond: 1.4,
  behaviour: 'approach',
  accuracyMetres: 8,
};

/** Read the simulator's configuration out of the query string. */
export function readSimOptions(search: string = window.location.search): SimOptions | null {
  const params = new URLSearchParams(search);
  if (params.get('sim') !== '1') return null;

  const number = (key: string, fallback: number): number => {
    const raw = params.get(key);
    if (raw === null) return fallback;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const behaviour = params.get('simBehaviour') ?? params.get('simBehavior');

  return {
    offsetMetres: number('simOffset', DEFAULT_SIM.offsetMetres),
    bearingDegrees: number('simBearing', DEFAULT_SIM.bearingDegrees),
    speedMetresPerSecond: number('simSpeed', DEFAULT_SIM.speedMetresPerSecond),
    behaviour:
      behaviour === 'wander' || behaviour === 'hold' || behaviour === 'approach'
        ? behaviour
        : DEFAULT_SIM.behaviour,
    accuracyMetres: number('simAccuracy', DEFAULT_SIM.accuracyMetres),
  };
}

/**
 * Carry the simulator's settings across an internal navigation.
 *
 * `/new?sim=1` redirects to `/s/CODE`, and the landing page links to `/new`; without this the
 * flag would be dropped at the first hop and the simulator would silently never start.
 */
export function withSimParams(path: string, search: string = window.location.search): string {
  const from = new URLSearchParams(search);
  if (from.get('sim') !== '1') return path;

  const [base, existing = ''] = path.split('?');
  const merged = new URLSearchParams(existing);
  for (const [key, value] of from) {
    if (key === 'sim' || key.startsWith('sim')) merged.set(key, value);
  }

  return `${base}?${merged.toString()}`;
}

/**
 * Advance the simulated walker by one step. Pure, so the behaviours can be tested without a
 * socket, a clock or a browser.
 */
export function stepPeer(
  from: LatLng,
  towards: LatLng | null,
  options: SimOptions,
  deltaSeconds: number,
  wanderSeed = 0,
): LatLng {
  if (options.behaviour === 'hold') return from;

  const distance = options.speedMetresPerSecond * deltaSeconds;

  if (options.behaviour === 'wander') {
    // A slow drift that keeps turning, so the arrow has something to chase without the peer
    // ever arriving. Deterministic in the seed, which makes it reproducible.
    const bearing = normalizeDegrees(wanderSeed * 37);
    return destinationPoint(from, bearing, distance);
  }

  if (!towards) return from;

  // Approach: walk straight at them, and stop once you are basically on top of them, so the
  // converged state is reachable and then stays put.
  const remaining = haversineDistance(from, towards);
  if (remaining <= 1) return from;

  return destinationPoint(from, initialBearing(from, towards), Math.min(distance, remaining));
}

export interface SimulatedPeer {
  /** Where the fake peer currently is — the debug panel shows it. */
  readonly position: LatLng | null;
  /** Tell the simulator where the real user is, so `approach` has something to walk at. */
  setTarget: (position: LatLng) => void;
  stop: () => void;
}

export interface StartSimOptions {
  code: string;
  options: SimOptions;
  /** Called with anything worth saying out loud while the simulator sets itself up. */
  log?: (message: string) => void;
}

/**
 * Claim the free slot and start walking.
 *
 * Returns null if the session is full — which is the right answer, because it means a real
 * second person is already there and the simulator would be the refused third party.
 */
export async function startSimulatedPeer(config: StartSimOptions): Promise<SimulatedPeer | null> {
  const { code, options, log } = config;

  let joined;
  try {
    joined = await api.joinSession(code, 'Sim');
  } catch (error) {
    log?.(`simulator could not join: ${error instanceof Error ? error.message : 'unknown'}`);
    return null;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const query = new URLSearchParams({ p: joined.participant, token: joined.token });
  const socket = new WebSocket(
    `${protocol}//${window.location.host}/ws/${code}?${query.toString()}`,
  );

  let target: LatLng | null = null;
  let position: LatLng | null = null;
  let timer: number | null = null;
  let ticks = 0;
  let stopped = false;

  const tick = (): void => {
    if (stopped) return;

    // Nothing to do until we know where the real user is: the peer is defined relative to them.
    if (!position && target) {
      position = destinationPoint(target, options.bearingDegrees, options.offsetMetres);
      log?.(`simulated peer starting ${options.offsetMetres} m away`);
    }

    if (position) {
      position = stepPeer(position, target, options, POSITION.minSendIntervalMs / 1000, ticks++);

      if (socket.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'pos',
            lat: position.lat,
            lng: position.lng,
            acc: options.accuracyMetres,
            // A simulated peer has no compass, and does not pretend to.
            heading: null,
            ts: Date.now(),
          }),
        );
      }
    }

    timer = window.setTimeout(tick, POSITION.minSendIntervalMs);
  };

  socket.addEventListener('open', () => {
    log?.('simulated peer connected');
    tick();
  });

  return {
    get position() {
      return position;
    },
    setTarget: (next) => {
      target = next;
    },
    stop: () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      socket.close();
    },
  };
}
